import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { OnePagerListing } from './pages/OnePagerListing';
import { OnePagerView } from './pages/OnePagerView';
import { OnePagerEditor } from './pages/OnePagerEditor';
import { ProfilePage } from './pages/ProfilePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { NewPasswordPage } from './pages/NewPasswordPage';
import { onePagerDataService } from './services/OnePagerDataService';
import { externalRouting } from './services/externalRouting';
import type { OnePager } from './types';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="loading">Loading...</div>;
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

// Main App Container
const AppContainer: React.FC = () => {
  const { login, signup, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [onePagers, setOnePagers] = useState<OnePager[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [usernameFilters, setUsernameFilters] = useState<string[]>([]);

  useEffect(() => {
    loadOnePagers();
  }, []);

  const loadOnePagers = async () => {
    try {
      setLoading(true);
      const data = await onePagerDataService.getAllOnePagers();
      setOnePagers(data);
    } catch (error) {
      console.error('Failed to load one-pagers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (credentials: any) => {
    try {
      await login(credentials);
      // Redirect to one-pager browser after successful login
      navigate('/');
    } catch (error) {
      alert('Login failed.');
    }
  };

  const handleSignup = async (credentials: any) => {
    try {
      await signup(credentials);
      // Redirect to one-pager browser after successful signup
      navigate('/');
    } catch (error) {
      alert('Signup failed. Please try again.');
    }
  };

  const handleCreateNew = () => {
    navigate('/create');
  };

  const handleCreateFromTemplate = (onePager: OnePager) => {
    navigate('/create', { state: { templateOnePager: onePager } });
  };

  return (
    <div className="app">
      <Header 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onCreateNew={handleCreateNew}
        usernameFilters={usernameFilters}
        onUsernameFiltersChange={setUsernameFilters}
      />
      <main className="main-content">
        <Routes>
          <Route 
            path="/login" 
            element={<LoginPage onLogin={handleLogin} loading={authLoading} />} 
          />
          <Route 
            path="/signup" 
            element={<SignupPage onSignup={handleSignup} loading={authLoading} />} 
          />
          <Route
            path="/"
            element={
              <OnePagerListing
                onePagers={onePagers}
                onView={(id) => navigate(`/o/${id}`)}
                onCreateFromTemplate={handleCreateFromTemplate}
                onCreateNew={handleCreateNew}
                loading={loading}
                searchTerm={searchTerm}
                usernameFilters={usernameFilters}
              />
            }
          />
          <Route
            path="/o/:id"
            element={<OnePagerViewWrapper onePagers={onePagers} onRefresh={loadOnePagers} />}
          />
          <Route
            path="/create"
            element={
              <ProtectedRoute>
                <OnePagerEditorWrapper onRefresh={loadOnePagers} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit/:id"
            element={
              <ProtectedRoute>
                <OnePagerEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/u/:username"
            element={<ProfilePage />}
          />
          <Route path="/reset-password-link" element={<ResetPasswordPage />} />
          <Route path="/reset-password" element={<NewPasswordPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

// Wrapper component for OnePager editor with template from state
const OnePagerEditorWrapper: React.FC<{ onRefresh: () => void }> = ({ onRefresh }) => {
  const location = useLocation();
  const templateOnePager = location.state?.templateOnePager as OnePager | undefined;
  
  return <OnePagerEditor templateOnePager={templateOnePager} onRefresh={onRefresh} />;
};

// Wrapper component for OnePager view with URL params
const OnePagerViewWrapper: React.FC<{ onePagers: OnePager[]; onRefresh: () => void }> = ({ onePagers, onRefresh }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [onePager, setOnePager] = useState<OnePager | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadOnePager(id);
    }
  }, [id]);

  const loadOnePager = async (onePagerId: string) => {
    try {
      setLoading(true);
      const data = await onePagerDataService.getOnePagerById(onePagerId);
      setOnePager(data);
    } catch (error) {
      console.error('Failed to load one-pager:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (onePagerId: string) => {
    // Refresh the main list
    onRefresh();
    // Navigate back to list
    navigate('/');
  };

  return (
    <OnePagerView
      onePager={onePager}
      onCreateFromTemplate={externalRouting.createFromTemplate}
      onBack={() => navigate('/')}
      onDelete={handleDelete}
      loading={loading}
    />
  );
};

export function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContainer />
      </Router>
    </AuthProvider>
  );
}

export default App;
